import { Component, Host, h, Prop, Element, State, Watch, Fragment } from "@stencil/core";
import {
  ViolatorSize,
  ViolatorFontSize,
  ViolatorFontStyle,
  ViolatorSchemeDefinition,
  ViolatorSchemeName,
  ViolatorPosition
} from "./constants/violator-types";
import {
  VIOLATOR_FONT_SIZE_LARGE,
  VIOLATOR_FONT_SIZE_NORMAL,
  VIOLATOR_FONT_TYPE_REGULAR,
  VIOLATOR_SCHEME_CONFIG,
  VIOLATOR_SCHEME_MAP
} from "./constants/violator-constants";
import pfx from "../../utils/style-class-prefix-helper";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { ViolatorSchemeConfiguration } from "../../constants/colors";
import {
  IS_SQUIRCLE,
  ViolatorAlignment,
  VIOLATOR_DEFAULT_ALIGNMENT,
  VIOLATOR_DEFAULT_PICTOGRAM_POSITION,
  VIOLATOR_DEFAULT_POSITION
} from "../../constants/global-constants";
import { getFirstNestedChild } from "../../utils/dom-helper";

/**
 * @slot [unnamed] - holds any number of HTML elements.
 */
@Component({
  tag: "eon-ui-violator",
  styleUrl: "violator.scss",
  shadow: true
})
export class Violator implements ColorableComponent<ViolatorSchemeConfiguration<ViolatorSchemeDefinition>> {
  schemeConfig: ViolatorSchemeConfiguration<ViolatorSchemeDefinition> = VIOLATOR_SCHEME_CONFIG;
  schemeMap = VIOLATOR_SCHEME_MAP;

  @Element() hostElement: HTMLEonUiViolatorElement;

  @State() activeScheme: ViolatorSchemeDefinition;

  @State() slottedMode: boolean = false;

  @State() isPictogramAvailable: boolean = false;
  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * Defines the size of the violator. Can be small or large. Large violators will be automaticall displayed as a small one on mobile breakpoint.
   */
  @Prop() size: ViolatorSize = VIOLATOR_FONT_SIZE_LARGE;

  /**
   * Defines the vertical text position
   * @example "top", "center", "bottom"
   * @default top
   */
  @Prop() positionY: ViolatorPosition = VIOLATOR_DEFAULT_POSITION;

  /**
   * Defines the text alignment.
   * @example "left", "center", "right"
   * @default left
   */
  @Prop() textAlignment: ViolatorAlignment = VIOLATOR_DEFAULT_ALIGNMENT;

  /**
   * The name of the color scheme of the component.
   */
  @Prop({ reflect: true }) scheme: ViolatorSchemeName;

  /**
   * Content of first violator text line.
   */
  @Prop() line1Content: string;

  /**
   * Font size of first violator text line.
   * @default VIOLATOR_FONT_SIZE_NORMAL
   */
  @Prop() line1FontSize: ViolatorFontSize = VIOLATOR_FONT_SIZE_NORMAL;

  /**
   * Font type of first violator text line.
   * @default VIOLATOR_FONT_TYPE_REGULAR
   */
  @Prop() line1FontType: ViolatorFontStyle = VIOLATOR_FONT_TYPE_REGULAR;

  /**
   * Content of second violator text line.
   */
  @Prop() line2Content: string;

  /**
   * Font size of second violator text line.
   * @default VIOLATOR_FONT_SIZE_NORMAL
   */
  @Prop() line2FontSize: ViolatorFontSize = VIOLATOR_FONT_SIZE_NORMAL;

  /**
   * Font type of second violator text line.
   * @default VIOLATOR_FONT_TYPE_REGULAR
   */
  @Prop() line2FontType: ViolatorFontStyle = VIOLATOR_FONT_TYPE_REGULAR;

  /**
   * Content of third violator text line.
   */
  @Prop() line3Content: string;

  /**
   * Font size of third violator text line.
   * @default VIOLATOR_FONT_SIZE_NORMAL
   */
  @Prop() line3FontSize: ViolatorFontSize = VIOLATOR_FONT_SIZE_NORMAL;

  /**
   * Font type of third violator text line.
   * @default VIOLATOR_FONT_TYPE_REGULAR
   */
  @Prop() line3FontType: ViolatorFontStyle = VIOLATOR_FONT_TYPE_REGULAR;

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.innerBackground = this.activeScheme.backgroundColor;
  }

  @Watch("scheme")
  applyScheme(): void {
    applySchemeDelegate(this);
  }

  componentWillLoad() {
    this.applyScheme();
    this.slottedMode = !!this.hostElement.children.length;
    this.isPictogramAvailable = !!getFirstNestedChild(this.hostElement, "eon-ui-pictogram");
  }

  componentDidLoad() {
    this.setCssVariables();
  }

  render() {
    return (
      <Host>
        <div
          class={pfx({
            ["inner"]: true,
            [this.size]: !!this.size,
            ["icon-no-svg"]: !IS_SQUIRCLE
          })}
        >
          <div
            class={pfx({
              ["content-wrapper"]: true,
              [`py-${this.positionY}`]: !!this.positionY && !this.isPictogramAvailable,
              [`t-${this.textAlignment}`]: !!this.textAlignment && !this.isPictogramAvailable,
              [`pictogram-${VIOLATOR_DEFAULT_PICTOGRAM_POSITION}`]: this.isPictogramAvailable
            })}
          >
            {this.slottedMode ? (
              <slot />
            ) : (
              <Fragment>
                <div
                  class={pfx({
                    ["line"]: true,
                    ["first-line"]: true,
                    [`fs-${this.line1FontSize}`]: !!this.line1FontSize,
                    [`ft-${this.line1FontType}`]: !!this.line1FontType
                  })}
                >
                  {this.line1Content}
                </div>
                <div
                  class={pfx({
                    ["line"]: true,
                    ["second-line"]: true,
                    [`fs-${this.line2FontSize}`]: !!this.line2FontSize,
                    [`ft-${this.line2FontType}`]: !!this.line2FontType
                  })}
                >
                  {this.line2Content}
                </div>
                <div
                  class={pfx({
                    ["line"]: true,
                    ["third-line"]: true,
                    [`fs-${this.line3FontSize}`]: !!this.line3FontSize,
                    [`ft-${this.line3FontType}`]: !!this.line3FontType
                  })}
                >
                  {this.line3Content}
                </div>
              </Fragment>
            )}
          </div>
          <div
            class={pfx({
              background: true,
              "icon-no-svg": !IS_SQUIRCLE
            })}
          >
            {IS_SQUIRCLE && (
              <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path
                  class={pfx("carrier solid")}
                  d="M7.768 1.544a7.818 7.818 0 0 0-6.224 6.224 86.081 86.081 0 0 0 0 32.464 7.818 7.818 0 0 0 6.224 6.224A86.694 86.694 0 0 0 24 48a86.694 86.694 0 0 0 16.232-1.544 7.818 7.818 0 0 0 6.224-6.224 86.081 86.081 0 0 0 0-32.464 7.818 7.818 0 0 0-6.224-6.224 86.1 86.1 0 0 0-32.464 0z"
                />
              </svg>
            )}
          </div>
        </div>
      </Host>
    );
  }
}
